'use client';
import { useEffect, useRef, useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { useCompleteTotpLogin, useLogin } from '../../lib/mutations';
import { ElowenApiError, elowenClient } from '../../lib/elowenClient';
import { safeSsoNext, ssoErrorCode } from '../../lib/authSso';
import { useToast } from '../ui/Toast';
import { Button, buttonClassName } from '../ui/Button';
import { Input } from '../ui/Input';
import { OneTimeCodeInput, type OneTimeCodeInputHandle } from '../ui/OneTimeCodeInput';
import { TotpCountdown } from '../ui/TotpCountdown';
import { useTranslation } from '../../lib/i18n';
import { ChettyLogo } from './ChettyLogo';

function MicrosoftLogo() {
  return (
    <svg viewBox="0 0 23 23" width="16" height="16" aria-hidden>
      <path fill="#f35325" d="M1 1h10v10H1z" />
      <path fill="#81bc06" d="M12 1h10v10H12z" />
      <path fill="#05a6f0" d="M1 12h10v10H1z" />
      <path fill="#ffba08" d="M12 12h10v10H12z" />
    </svg>
  );
}

export function LoginForm({ onAuthed }: { onAuthed: () => void }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const login = useLogin();
  const complete = useCompleteTotpLogin();
  const [challengeExpiry, setChallengeExpiry] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [codeError, setCodeError] = useState('');
  const codeRef = useRef<OneTimeCodeInputHandle>(null);
  const usernameRef = useRef<HTMLInputElement>(null);
  useEffect(() => { if (challengeExpiry) codeRef.current?.focus(); else usernameRef.current?.focus(); }, [challengeExpiry]);
  // The provider list is PUBLIC instance metadata, not session data, so it deliberately does not live in
  // the react-query cache: any 401 calls clearToken(), and LoginGate's AUTH_CLEARED_EVENT handler answers
  // with qc.clear(), wiping the cache wholesale. The login screen provokes those 401s itself (/auth/me,
  // /config) while this list is in flight, so it was a race the list usually LOST — measured live, clear
  // ran at 160ms and 168ms against a response at 178ms. A cached list is dropped on arrival when its
  // query is already gone, and nothing refetches it, so the button silently never appeared.
  const [ssoProviders, setSsoProviders] = useState<{ id: string; label: string }[]>([]);
  useEffect(() => {
    let cancelled = false;
    void elowenClient.ssoProviders()
      .then((list) => { if (!cancelled) setSsoProviders(list); })
      // No reachable provider list means SSO is simply not offered; the password form stands on its own.
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, []);
  const { toast } = useToast();
  const { t } = useTranslation();
  const router = useRouter();
  const searchParams = useSearchParams();
  const shownError = useRef<string | null>(null);
  const error = searchParams.get('sso_error');

  useEffect(() => {
    if (!error || shownError.current === error) return;
    shownError.current = error;
    const code = ssoErrorCode(error);
    toast(t.auth.ssoErrors[code], 'error');
    const nextParams = new URLSearchParams(searchParams.toString());
    nextParams.delete('sso_error');
    const query = nextParams.toString();
    router.replace(`${window.location.pathname}${query ? `?${query}` : ''}`);
  }, [error, router, searchParams, t.auth.ssoErrors, toast]);

  async function cancelChallenge() {
    setCode('');
    setPassword('');
    setCodeError('');
    setVerifying(true);
    login.reset();
    try {
      await elowenClient.cancelTotpLogin();
      setChallengeExpiry(null);
    } catch {
      setCodeError(t.totp.actionError);
    } finally {
      setVerifying(false);
    }
  }

  async function completeChallenge(entered: string) {
    if (verifying) return;
    setVerifying(true);
    setCodeError('');
    try {
      await complete.mutateAsync(entered);
      setCode('');
      onAuthed();
    } catch (error) {
      const seconds = error instanceof ElowenApiError ? Number(error.details?.retryAfter) : NaN;
      setCodeError(error instanceof ElowenApiError && error.status === 429
        ? Number.isFinite(seconds) && seconds > 0 ? t.totp.rateRetry.replace('{seconds}', String(seconds)) : t.auth.loginRateLimited
        : !(error instanceof ElowenApiError) || error.status >= 500 ? t.common.daemonUnreachable : t.totp.invalid);
      codeRef.current?.focus();
    } finally {
      complete.reset();
      setVerifying(false);
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    login.mutate(
      { username, password },
      {
        onSuccess: (result) => {
          setPassword('');
          login.reset();
          if (result.status === 'totp_required') {
            setCodeError('');
            setChallengeExpiry(result.expiresAt);
          } else {
            // Only the final response establishes the BFF-owned session.
            onAuthed();
          }
        },
        onError: (error) => {
          // Only a 401 says the credentials were wrong. The daemon also answers 429 to a throttled origin,
          // and a 5xx or a transport failure means the service could not be asked at all.
          if (error instanceof ElowenApiError && error.status === 429) {
            const seconds = Number(error.details?.retryAfter);
            toast(Number.isFinite(seconds) && seconds > 0 ? t.totp.rateRetry.replace('{seconds}', String(seconds)) : t.auth.loginRateLimited, 'error');
          }
          else if (!(error instanceof ElowenApiError) || error.status >= 500) toast(t.common.daemonUnreachable, 'error');
          else toast(t.auth.loginFailed, 'error');
        },
      },
    );
  }

  const currentTarget = typeof window === 'undefined'
    ? '/'
    : safeSsoNext(`${window.location.pathname}${window.location.search}`);
  const showMicrosoft = ssoProviders.length > 0;

  return (
    <div className="login-page flex min-h-screen items-center justify-center bg-background px-4 py-10">
      <main className="login-card animate-pop-in flex w-full max-w-md flex-col gap-6 rounded-xl border border-border bg-card p-8 shadow-raised">
        <div className="flex flex-col items-center gap-5 text-center">
          <ChettyLogo className="w-56" />
          <div className="space-y-1.5">
            <h1 className="text-page-title text-foreground">{challengeExpiry ? t.totp.loginTitle : t.auth.signIn}</h1>
            <p className="text-meta text-muted-foreground">{t.auth.signInDescription}</p>
          </div>
        </div>

        {challengeExpiry ? (
          <form onSubmit={(e) => { e.preventDefault(); void completeChallenge(code); }} className="flex flex-col gap-4" aria-busy={verifying}>
            {/* The code step is centred like the card's heading; the boxes keep their own width. */}
            <div className="flex flex-col items-center gap-2 text-center text-card-title text-foreground">
              <span aria-hidden>{t.totp.code}</span>
              <OneTimeCodeInput ref={codeRef} label={t.totp.code} value={code} onChange={setCode}
                onComplete={(entered) => void completeChallenge(entered)}
                aria-describedby="login-totp-help login-totp-error" invalid={Boolean(codeError)} />
              <TotpCountdown />
            </div>
            <p id="login-totp-help" className="text-center text-meta text-muted-foreground">{t.totp.lost}</p>
            <p id="login-totp-error" className="text-center text-meta text-destructive" aria-live="polite">{codeError}</p>
            <Button type="submit" variant="accent" size="lg" disabled={verifying || code.length !== 6}>{t.totp.verify}</Button>
            <Button type="button" variant="ghost" disabled={verifying} onClick={() => void cancelChallenge()}>{t.common.back}</Button>
          </form>
        ) : <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <label className="flex flex-col gap-2 text-card-title text-foreground">
            <span>{t.auth.usernamePlaceholder}</span>
            <Input ref={usernameRef} type="text" placeholder={t.auth.usernamePlaceholder} value={username} onChange={(e) => setUsername(e.target.value)} autoComplete="username" />
          </label>
          <label className="flex flex-col gap-2 text-card-title text-foreground">
            <span>{t.auth.passwordPlaceholder}</span>
            <Input type="password" placeholder={t.auth.passwordPlaceholder} value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" />
          </label>
          <Button type="submit" variant="accent" size="lg" disabled={login.isPending} className="w-full justify-center">
            {t.auth.signIn}
          </Button>
        </form>}

        {showMicrosoft ? (
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-3 text-ui text-muted-foreground" aria-hidden>
              <span className="h-px flex-1 bg-border" />
              <span>{t.auth.orContinueWith}</span>
              <span className="h-px flex-1 bg-border" />
            </div>
            <a
              href={`/api/auth/sso/microsoft/start?next=${encodeURIComponent(currentTarget)}`}
              aria-disabled={login.isPending || verifying}
              tabIndex={login.isPending || verifying ? -1 : undefined}
              onClick={(e) => { if (login.isPending || verifying) e.preventDefault(); else { setPassword(''); setCode(''); } }}
              className={buttonClassName('default', 'lg', 'w-full justify-center')}
            >
              <MicrosoftLogo />
              {t.auth.microsoft}
            </a>
          </div>
        ) : null}
      </main>
    </div>
  );
}
